import React, { useState, useRef, useEffect } from 'react';
import './WaiverModal.css';

const WaiverRegistrationModal = ({ userName, onComplete, onCancel }) => {
  const [step, setStep] = useState(1);
  const [error, setError] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.strokeStyle = '#1e40af';
    }
  }, [step]);

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    
    setIsDrawing(true);
    const x = e.clientX ? e.clientX - rect.left : e.touches[0].clientX - rect.left;
    const y = e.clientY ? e.clientY - rect.top : e.touches[0].clientY - rect.top;
    
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    
    const x = e.clientX ? e.clientX - rect.left : e.touches[0].clientX - rect.left;
    const y = e.clientY ? e.clientY - rect.top : e.touches[0].clientY - rect.top;
    
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const handleNext = () => {
    setError('');
    setStep(2);
  };

  const handleSubmit = () => {
    if (!acknowledged) {
      setError('Please acknowledge that you have read and agree to the waiver');
      return;
    }

    if (!hasSignature) {
      setError('Please provide your signature');
      return;
    }

    const canvas = canvasRef.current;
    const signatureData = canvas.toDataURL('image/png');

    // Pass signature data back to parent
    onComplete({
      signature: signatureData,
      acknowledged: acknowledged,
      guardian_name: userName
    });
  };

  const getWaiverContent = () => {
    return (
      <div className="waiver-content-scroll">
        <div className="waiver-section">
          <h2 style={{textAlign: 'center', marginBottom: '20px'}}>AMATEUR ATHLETIC WAIVER AND RELEASE OF LIABILITY</h2>
          
          <h3 style={{textDecoration: 'underline'}}>READ BEFORE SIGNING</h3>
          
          <p>In consideration of being allowed to participate in any way in Redmond Cow Corner LLC (DBA: MSK Prasad's International Cricket Academy - Seattle) athletic sports program, related events and activities, the undersigned acknowledges, appreciates, and agrees that:</p>
          
          <ol style={{marginLeft: '20px'}}>
            <li style={{marginBottom: '15px'}}>
              The risks of injury and illness (ex: communicable diseases such as MRSA, influenza, and COVID-19) from the activities involved in this program are significant, including the potential for permanent paralysis and death, and while particular rules, equipment, and personal discipline may reduce these risks, the risks of serious injury and illness do exist; and,
            </li>
            
            <li style={{marginBottom: '15px'}}>
              I KNOWINGLY AND FREELY ASSUME ALL SUCH RISKS, both known and unknown, EVEN IF ARISING FROM THE NEGLIGENCE OF THE RELEASEES or others, and assume full responsibility for my participation; and,
            </li>
            
            <li style={{marginBottom: '15px'}}>
              I willingly agree to comply with the stated and customary terms and conditions for participation. If, however, I observe any unusual significant hazard during my presence or participation, I will remove myself from participation and bring such to the attention of the nearest official immediately; and,
            </li>
            
            <li style={{marginBottom: '15px'}}>
              I, for myself, my heirs, assigns, personal representatives and next of kin, HEREBY RELEASE AND HOLD HARMLESS Redmond Cow Corner LLC (DBA: MSK Prasad's International Cricket Academy - Seattle) their officers, officials, agents, and/or employees, other participants, sponsoring agencies, sponsors, advertisers, and if applicable, owners and lessors of premises used to conduct the event ("RELEASEES"), WITH RESPECT TO ANY AND ALL INJURY, ILLNESS, DISABILITY, DEATH, or loss or damage to person or property, WHETHER ARISING FROM THE NEGLIGENCE OF THE RELEASEES OR OTHERWISE, to the fullest extent permitted by law.
            </li>
          </ol>
          
          <p style={{fontWeight: 'bold', marginTop: '20px'}}>
            I HAVE READ THIS RELEASE OF LIABILITY AND ASSUMPTION OF RISK AGREEMENT, FULLY UNDERSTAND ITS TERMS, UNDERSTAND THAT I HAVE GIVEN UP SUBSTANTIAL RIGHTS BY SIGNING IT, AND SIGN IT FREELY AND VOLUNTARILY WITHOUT ANY INDUCEMENT.
          </p>
          
          <div style={{marginTop: '30px', padding: '15px', backgroundColor: '#f0f9ff', borderRadius: '8px'}}>
            <p style={{marginBottom: '10px'}}><strong>Participant:</strong></p>
            <p style={{fontSize: '16px', color: '#1e40af'}}>{userName}</p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="waiver-modal-overlay">
      <div className="waiver-modal">
        <div className="waiver-header">
          <h2>🏏 Adult Waiver Agreement Required</h2>
          <p>Please review and sign the waiver to complete registration</p>
        </div>

        {error && (
          <div className="waiver-error">
            <span className="error-icon">⚠️</span>
            <span>{error}</span>
          </div>
        )}

        {step === 1 && (
          <div className="waiver-step">
            {getWaiverContent()}

            <div className="waiver-actions" style={{marginTop: '20px'}}>
              <button className="btn-secondary" onClick={onCancel}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleNext}>
                Next: Sign Waiver
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="waiver-step">
            <div className="signature-section">
              <h4>Digital Signature</h4>
              <p className="signature-instruction">
                By signing below, you acknowledge that you have read, understood, and agree to the terms of the waiver above.
              </p>
              
              <div className="signature-canvas-container">
                <canvas
                  ref={canvasRef}
                  width={600}
                  height={200}
                  className="signature-canvas"
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                />
                {!hasSignature && (
                  <div className="signature-placeholder">Sign here</div>
                )}
              </div>
              
              <button 
                className="btn-clear" 
                onClick={clearSignature}
                type="button"
              >
                Clear Signature
              </button>
            </div>

            <div className="acknowledgment-section">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                />
                <span>
                  I, <strong>{userName}</strong>, acknowledge that I have read and understood the waiver above. 
                  I agree to its terms and sign freely and voluntarily.
                </span>
              </label>
            </div>

            <div className="waiver-actions">
              <button 
                className="btn-secondary" 
                onClick={() => setStep(1)}
              >
                Back
              </button>
              <button 
                className="btn-primary" 
                onClick={handleSubmit}
                disabled={!acknowledged || !hasSignature}
              >
                Submit & Complete Registration
              </button>
            </div>
          </div>
        )}

        <div className="waiver-footer">
          <p>
            <strong>Note:</strong> This waiver is required to complete your registration. 
            A copy will be sent to your email address for your records.
          </p>
        </div>
      </div>
    </div>
  );
};

export default WaiverRegistrationModal;
